import { getItemName, type ItemTypeValue } from '@dreamgame/shared';
import { useEffect, useRef, useState } from 'preact/hooks';
import { tv } from 'tailwind-variants';
import {
  type ContextMenuAction,
  closeContextMenu,
  contextMenuActions,
  contextMenuPosition,
  contextMenuTarget,
  executeAction,
  isContextMenuOpen,
} from '../../state/contextMenu';
import { t } from '../../state/i18n';
import { ActionTooltip } from './ActionTooltip';

// ============================================================================
// Styles
// ============================================================================

const menuItem = tv({
  base: [
    'context-menu-item',
    'w-full text-left px-3 py-1.5',
    'font-medieval text-sm tracking-wide',
    'transition-all duration-100 ease-out',
    'border-b border-rs-stone/40 last:border-b-0',
    'flex items-center justify-between gap-2',
  ],
  variants: {
    color: {
      default: 'text-rs-parchment hover:bg-rs-stone-light/30',
      danger: 'text-rs-dragon hover:bg-rs-dragon/20',
      success: 'text-emerald-400 hover:bg-emerald-500/20',
      warning: 'text-rs-gold hover:bg-rs-gold/20',
    },
    disabled: {
      true: 'opacity-50',
      false: 'cursor-pointer hover:pl-4',
    },
    hasReasons: {
      true: '',
      false: '',
    },
    isFirst: {
      true: 'font-semibold',
      false: '',
    },
  },
  compoundVariants: [
    {
      disabled: true,
      hasReasons: true,
      class: 'cursor-pointer',
    },
    {
      disabled: true,
      hasReasons: false,
      class: 'cursor-not-allowed',
    },
  ],
  defaultVariants: {
    color: 'default',
    disabled: false,
    hasReasons: false,
    isFirst: false,
  },
});

// Info icon SVG component
const InfoIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    class="text-rs-gold/80 flex-shrink-0"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

// ============================================================================
// Components
// ============================================================================

// Single menu item with tooltip support for disabled actions
const ContextMenuItem = ({ action, isFirst }: { action: ContextMenuAction; isFirst: boolean }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const iconRef = useRef<HTMLButtonElement>(null);
  const hoverTimeoutRef = useRef<number | null>(null);

  const hasReasons = action.disabledReasons && action.disabledReasons.length > 0;
  const showTooltip = hasReasons && (isHovered || isPinned);

  const getIconRect = () => {
    return iconRef.current?.getBoundingClientRect() ?? null;
  };

  const handleIconPointerEnter = () => {
    if (isPinned) {
      return;
    }

    // Clear any existing timeout
    if (hoverTimeoutRef.current !== null) {
      window.clearTimeout(hoverTimeoutRef.current);
    }

    // Show tooltip after delay
    hoverTimeoutRef.current = window.setTimeout(() => {
      setIsHovered(true);
      hoverTimeoutRef.current = null;
    }, 100);
  };

  const handleIconPointerLeave = () => {
    // Clear pending timeout
    if (hoverTimeoutRef.current !== null) {
      window.clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    if (!isPinned) {
      setIsHovered(false);
    }
  };

  const handleIconClick = (e: MouseEvent) => {
    e.stopPropagation();
    // Toggle pinned state
    setIsPinned(!isPinned);
    setIsHovered(false);
  };

  const handleButtonClick = () => {
    if (!action.disabled) {
      executeAction({ action });
    }
  };

  const handleCloseTooltip = () => {
    setIsPinned(false);
    setIsHovered(false);
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current !== null) {
        window.clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const iconRect = showTooltip ? getIconRect() : null;

  return (
    <div class="relative flex">
      <button
        type="button"
        onClick={handleButtonClick}
        class={menuItem({
          color: action.color ?? 'default',
          disabled: action.disabled ?? false,
          hasReasons: false,
          isFirst,
        })}
        style={{ flex: '1 1 auto' }}
      >
        {action.label}
      </button>
      {hasReasons && (
        <button
          ref={iconRef}
          type="button"
          onPointerEnter={handleIconPointerEnter}
          onPointerLeave={handleIconPointerLeave}
          onClick={handleIconClick}
          class="px-2 flex items-center cursor-pointer hover:bg-rs-stone-light/30 border-b border-rs-stone/40 last:border-b-0"
          aria-label="Show requirements"
        >
          <InfoIcon />
        </button>
      )}

      {showTooltip && action.disabledReasons && (
        <ActionTooltip
          reasons={action.disabledReasons}
          isPinned={isPinned}
          onClose={handleCloseTooltip}
          parentRect={iconRect}
        />
      )}
    </div>
  );
};

// Menu header showing target info
const ContextMenuHeader = () => {
  const target = contextMenuTarget.value;

  if (!target) {
    return null;
  }

  const getGroundItemTitle = (): string => {
    if (!target.groundItems || target.groundItems.length === 0) {
      return t('ui.context.item');
    }
    if (target.groundItems.length === 1) {
      const item = target.groundItems[0];
      if (item) {
        return getItemName({ itemType: item.itemType as ItemTypeValue });
      }
    }
    return `Items (${target.groundItems.length})`;
  };

  const getTitleByType: Record<string, () => string> = {
    construction: () =>
      target.objectName
        ? t('ui.context.blueprint', { name: target.objectName })
        : t('ui.context.construction'),
    object: () => target.objectName ?? t('ui.context.object'),
    ground: () => t('ui.context.ground'),
    pawn: () => t('ui.context.colonist'),
    animal: () => target.entityName ?? t('ui.context.animal'),
    groundItem: getGroundItemTitle,
    inventoryItem: () => target.inventoryItem?.itemName ?? t('ui.context.item'),
  };

  const getTitle = (): string => {
    const titleFn = getTitleByType[target.type];
    return titleFn ? titleFn() : t('ui.context.unknown');
  };

  const title = getTitle();

  return (
    <div
      class="
        context-menu-header
        px-3 py-2
        bg-rs-stone-dark/80
        border-b-2 border-rs-gold/60
        font-medieval font-bold text-sm
        text-rs-gold tracking-wider
        text-center
        uppercase
      "
    >
      {title}
    </div>
  );
};

export const ContextMenu = () => {
  const menuRef = useRef<HTMLDivElement>(null);

  const isOpen = isContextMenuOpen.value;
  const position = contextMenuPosition.value;
  const actions = contextMenuActions.value;

  // Close on click outside
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        closeContextMenu();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeContextMenu();
      }
    };

    // Delay adding listener to prevent immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Adjust position if menu would go off screen
  useEffect(() => {
    if (!isOpen || !menuRef.current) {
      return;
    }

    const menu = menuRef.current;
    const rect = menu.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Check if menu goes off right edge
    if (rect.right > viewportWidth) {
      menu.style.left = `${viewportWidth - rect.width - 8}px`;
    }

    // Check if menu goes off bottom edge
    if (rect.bottom > viewportHeight) {
      menu.style.top = `${viewportHeight - rect.height - 8}px`;
    }
  }, [isOpen, position]);

  if (!isOpen || actions.length === 0) {
    return null;
  }

  return (
    <div
      ref={menuRef}
      class="
        context-menu
        fixed z-[2000]
        min-w-[160px] max-w-[220px]
        animate-context-menu-appear
      "
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      {/* Outer stone border */}
      <div
        class="
          rounded-sm
          bg-gradient-to-b from-rs-stone-light via-rs-stone to-rs-stone-dark
          p-[2px]
          shadow-rs-panel
        "
      >
        {/* Inner dark border */}
        <div
          class="
            rounded-sm
            bg-gradient-to-b from-black/60 to-black/40
            p-[1px]
          "
        >
          {/* Menu content area */}
          <div
            class="
              rounded-sm
              bg-gradient-to-b from-rs-stone via-rs-stone-dark to-[#2a2218]
              overflow-hidden
            "
          >
            <ContextMenuHeader />

            <div class="py-1">
              {actions.map((action, index) => (
                <ContextMenuItem
                  key={action.id}
                  action={action}
                  isFirst={index === 0 && action.color === 'success'}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Decorative corner accents */}
      <div class="absolute -top-[1px] -left-[1px] w-2 h-2 border-t-2 border-l-2 border-rs-gold/50 rounded-tl-sm" />
      <div class="absolute -top-[1px] -right-[1px] w-2 h-2 border-t-2 border-r-2 border-rs-gold/50 rounded-tr-sm" />
      <div class="absolute -bottom-[1px] -left-[1px] w-2 h-2 border-b-2 border-l-2 border-rs-gold/50 rounded-bl-sm" />
      <div class="absolute -bottom-[1px] -right-[1px] w-2 h-2 border-b-2 border-r-2 border-rs-gold/50 rounded-br-sm" />
    </div>
  );
};
